/**
 * Type a name in directly, rather than waiting for the generator to find it.
 *
 * Suggestions are drawn from the corpus so a name we already know arrives
 * with its origin and meaning attached. Anything not on the list is still
 * accepted — the list is three and a half thousand names, not every name.
 */
import { useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import type { NameRecord } from '@/lib/types.ts'
import { useLookup, type Filter } from './useNames.ts'
import { ordinal } from '@/lib/format.ts'

type Props = {
  all: NameRecord[]
  filter: Filter
  onPick: (record: NameRecord) => void
}

export function NameEntry({ all, filter, onPick }: Props) {
  const { resolve, suggest } = useLookup(all, filter)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(-1)
  const [open, setOpen] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const suggestions = useMemo(() => (open ? suggest(query) : []), [open, query, suggest])

  function choose(record: NameRecord | null) {
    if (!record) return
    onPick(record)
    setQuery('')
    setHighlight(-1)
    setOpen(false)
    input.current?.blur()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    // An arrowed-to suggestion wins; otherwise take them at their word.
    choose(highlight >= 0 ? suggestions[highlight] : resolve(query))
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false)
      setHighlight(-1)
      return
    }
    if (suggestions.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlight((h) => (h + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1))
    }
  }

  return (
    <form onSubmit={submit} className="relative w-full max-w-[22rem]">
      <label className="sr-only" htmlFor="name-entry">Type a name</label>
      <div className="flex items-center gap-2">
        <input
          id="name-entry"
          ref={input}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(-1) }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          placeholder="Or type one you've thought of…"
          autoComplete="off"
          spellCheck={false}
          className="field py-2.5 text-[0.95rem]"
        />
        <button
          type="submit"
          disabled={query.trim().length === 0}
          className="btn-ghost shrink-0 px-4 py-2.5 text-[0.85rem]"
        >
          Look
        </button>
      </div>

      {suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-leaf border border-linen bg-paper shadow-lift"
        >
          {suggestions.map((record, i) => (
            <li key={record.name}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlight}
                // onMouseDown, not onClick — blur fires first and would close this.
                onMouseDown={(e) => { e.preventDefault(); choose(record) }}
                onMouseEnter={() => setHighlight(i)}
                className={[
                  'flex w-full items-baseline gap-2 px-4 py-2 text-left transition-colors',
                  i === highlight ? 'bg-parchment' : 'hover:bg-parchment/60',
                ].join(' ')}
              >
                <span className="font-display text-[1.05rem]">{record.name}</span>
                {record.meaning && (
                  <span className="truncate text-[0.78rem] text-whisper">{record.meaning}</span>
                )}
                {record.rank && (
                  <span className="ml-auto shrink-0 text-[0.68rem] text-whisper">
                    {ordinal(record.rank)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </form>
  )
}
